<template>
    <b-modal :id="modalId" size="xl" title="Create New Case" ok-title="Save Case"
        @show="handleModalShow" @ok="saveCase" @hidden="$emit('hidden')">
        <!-- Prescription selector (optional) -->
        <b-row v-if="prescriptions.length > 0" class="mb-3">
            <b-col>
                <label><strong>Related Prescriptions (optional):</strong></label>
                <div class="prescription-selector" style="max-height: 200px; overflow-y: auto;">
                    <b-table
                        :items="prescriptions"
                        :fields="prescriptionFields"
                        striped
                        small
                        selectable
                        select-mode="multi"
                        class="mb-0"
                        @row-selected="onPrescriptionSelected"
                        ref="prescriptionTable">
                        <template #cell(selected)="{ rowSelected }">
                            <template v-if="rowSelected">
                                <i class="fa fa-check-square text-primary"></i>
                            </template>
                            <template v-else>
                                <i class="far fa-square text-muted"></i>
                            </template>
                        </template>
                        <template #cell(patientPay)="data">
                            {{ data.value | currency }}
                        </template>
                    </b-table>
                </div>
            </b-col>
        </b-row>

        <!-- Preselected prescriptions display (when passed) -->
        <b-row v-if="preselectedPrescriptions.length > 0 && prescriptions.length === 0">
            <b-col>
                <b-table :items="preselectedPrescriptions" :fields="prescriptionFields"
                    striped responsive small>
                    <template #cell(patientPay)="data">
                        {{ data.value | currency }}
                    </template>
                </b-table>
            </b-col>
        </b-row>

        <b-row>
            <b-col :lg="caseCategories.length > 0 ? 4 : 6" cols="12">
                <label><strong>Problem:</strong></label>
                <b-form-select v-model="selectedProblem" :options="problemOptions" />
            </b-col>
            <b-col v-if="caseCategories.length > 0" lg="4" cols="12">
                <label><strong>Category:</strong></label>
                <b-form-select v-model="selectedCategory" :options="categoryOptions" />
            </b-col>
            <b-col lg="4" cols="12">
                <label><strong>Store:</strong></label>
                <b-form-select v-model="selectedStoreId" :options="storeOptions" />
            </b-col>
        </b-row>

        <b-row class="mt-3">
            <b-col lg="6" cols="12">
                <CaseAssignmentComponent v-model="caseAssignment" label="Assign To" />
            </b-col>
            <b-col lg="6" cols="12">
                <CasesWatcherComponent v-model="caseWatchers" label="Case Watchers" :disabled="false"
                    :show-add-me-link="true">
                    <template #help-text>
                        <HelpPopup>
                            Select users and teams to watch this case
                        </HelpPopup>
                    </template>
                </CasesWatcherComponent>
            </b-col>
        </b-row>

        <b-row class="mt-3">
            <b-col>
                <label><strong>Notes:</strong></label>
                <b-form-textarea style="max-height:100%" rows="5" v-model="problemNote" />
            </b-col>
        </b-row>

        <b-row class="mt-3">
            <b-col>
                <hr>
                <b-form-group label="Question Set:" class="mb-3">
                    <b-select v-model="selectedCaseQuestionSetId" :options="caseQuestionSetOptions" />
                </b-form-group>
                <TaskQuestions ref="taskQuestionsComponent"
                    v-if="selectedCaseQuestionSetId === 0 || selectedCaseQuestionSetId"
                    :questions="caseQuestions" @input="updateCaseQuestions" :answers="caseAnswers"
                    :editing="selectedCaseQuestionSetId === 0" :show-errors="showCaseQuestionErrors" />
            </b-col>
        </b-row>
    </b-modal>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch, Ref } from 'vue-property-decorator';
import axios from 'axios';
import { BvModalEvent } from 'bootstrap-vue';
import { Patient } from '@/models/Patient';
import { Prescription } from '@/models/Prescription';
import { CaseNote, CaseWatcher } from '@/models/Case';
import { Store } from '@/models/Store';
import { UserTaskQuestion, UserTaskAnswer, UserTaskQuestionSet, UserTaskQuestionType } from '@/models/UserTask';
import { EntityType } from '@/models/EntityType';
import { CaseProblemConfig } from '@/models/Configuration';
import { Permission } from '@/models/Permission';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import CasesWatcherComponent from '@/components/Common/CasesWatcherComponent.vue';
import CaseAssignmentComponent from '@/components/Common/CaseAssignmentComponent.vue';
import HelpPopup from '@/components/HelpPopup.vue';

@Component({
    name: 'CreateCaseModal',
    components: {
        CasesWatcherComponent,
        CaseAssignmentComponent,
        HelpPopup,
        TaskQuestions: () => import('@/components/Tasks/TaskQuestions.vue')
    }
})
export default class CreateCaseModal extends Vue {
    @Prop({ required: true }) patient!: Patient;
    @Prop({ default: () => [] }) prescriptions!: Prescription[];
    @Prop({ default: () => [] }) preselectedPrescriptions!: Prescription[];
    @Prop({ default: 'create-case-modal' }) modalId!: string;

    @Ref('taskQuestionsComponent') taskQuestionsComponent: any | null = null;
    @Ref('prescriptionTable') prescriptionTable: any | null = null;

    // Form state
    protected selectedProblem: string | null = null;
    protected selectedCategory: string | null = null;
    protected caseAssignment: any = null;
    protected problemNote: string = '';
    protected caseWatchers: CaseWatcher[] = [];
    protected selectedPrescriptions: Prescription[] = [];

    // API data
    protected problemConfigs: CaseProblemConfig[] = [];
    protected caseCategories: any[] = [];
    protected caseQuestionSets: UserTaskQuestionSet[] = [];
    protected stores: Store[] = [];
    protected selectedStoreId: number | null = null;

    // Question set state
    protected selectedCaseQuestionSetId: number | null = null;
    protected caseQuestions: UserTaskQuestion[] = [];
    protected caseAnswers: UserTaskAnswer[] = [];
    protected showCaseQuestionErrors = false;

    // Prescription table fields
    protected prescriptionFields = [
        { key: 'selected', label: '', sortable: false },
        { key: 'rxID', label: 'Rx ID', sortable: false },
        { key: 'drugName', label: 'Drug', sortable: false },
        { key: 'patientPay', label: 'Patient Pay', sortable: false }
    ];

    get problemOptions() {
        return [
            { value: null, text: 'Select A Problem' },
            ...this.problemConfigs.map(config => config.name)
        ];
    }

    get categoryOptions() {
        return [
            { value: null, text: 'Select A Category' },
            ...this.caseCategories.map((cat: any) => ({ value: cat.name, text: cat.name }))
        ];
    }

    get storeOptions() {
        return [
            { value: null, text: '' },
            ...this.stores.map(store => ({ value: store.id, text: store.name }))
        ];
    }

    get caseQuestionSetOptions() {
        const options = [
            { value: null, text: 'None' },
            ...this.caseQuestionSets.map(qs => ({ value: qs.id, text: qs.name }))
        ];

        const hasCustomQuestionSetPermission = this.$permissions.some(
            (p: Permission) => p.permissionCodeName === 'Compliance/Create Custom Case Question Sets'
        );

        if (hasCustomQuestionSetPermission) {
            options.push({ value: 0, text: 'Custom' });
        }

        return options;
    }

    async handleModalShow() {
        this.getCaseProblems();
        await this.getCaseCategories();
        this.getStores();
        this.getCaseQuestionSets();
        this.resetForm();

        // Handle preselected prescriptions
        if (this.preselectedPrescriptions.length > 0) {
            this.selectedPrescriptions = [...this.preselectedPrescriptions];
            // Auto-populate store from first preselected prescription
            this.selectedStoreId = this.preselectedPrescriptions[0].storeID ?? null;
        }

        // Select preselected rows in the table if prescriptions are provided
        this.$nextTick(() => {
            if (this.prescriptionTable && this.preselectedPrescriptions.length > 0) {
                this.preselectedPrescriptions.forEach(rx => {
                    const index = this.prescriptions.findIndex(p => p.rxID === rx.rxID);
                    if (index >= 0) {
                        this.prescriptionTable.selectRow(index);
                    }
                });
            }
        });
    }

    resetForm() {
        this.selectedCaseQuestionSetId = null;
        this.caseQuestions = [];
        this.caseAnswers = [];
        this.selectedCategory = this.caseCategories.length > 0 ? this.caseCategories[0].name : null;
        this.caseAssignment = null;
        this.selectedProblem = null;
        this.problemNote = '';
        this.caseWatchers = [];
        this.selectedPrescriptions = [];
        this.showCaseQuestionErrors = false;
        this.selectedStoreId = null;
    }

    async getCaseProblems() {
        try {
            const resp = await axios.get<CaseProblemConfig[]>('/Case/problem-configs');
            this.problemConfigs = resp.data;
        } catch (err) {
            console.error(err);
        }
    }

    async getCaseCategories() {
        try {
            const resp = await axios.get<any[]>('/Case/categories');
            this.caseCategories = resp.data;
        } catch (err) {
            console.error(err);
        }
    }

    async getStores() {
        try {
            const resp = await axios.get<Store[]>('/Store/');
            this.stores = resp.data;
        } catch (err) {
            console.error(err);
        }
    }

    async getCaseQuestionSets() {
        try {
            const resp = await this.$http.get<UserTaskQuestionSet[]>(`/task/question-sets/${EntityType.Case}`);
            this.caseQuestionSets = resp.data.filter(qs => qs.name && qs.active);
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error(err);
        }
    }

    @Watch('selectedCaseQuestionSetId')
    async getQuestionSet() {
        try {
            if (!this.selectedCaseQuestionSetId || this.selectedCaseQuestionSetId === 0) {
                this.caseQuestions = [];
                this.caseAnswers = [];
                return;
            }
            const resp = await this.$http.get<UserTaskQuestionSet>(`/task/question-set/${this.selectedCaseQuestionSetId}`);
            this.caseQuestions = resp.data.questions;
            this.caseAnswers = [];
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error(err);
        }
    }

    @Watch('selectedProblem')
    handleSelectedProblemChange() {
        if (!this.selectedProblem) {
            this.selectedCaseQuestionSetId = null;
            this.caseWatchers = [];
            this.caseAssignment = null;
            return;
        }

        // Auto-populate question set
        const defaultQuestionSet = this.caseQuestionSets.find(qs => qs.defaultScope === this.selectedProblem);
        this.selectedCaseQuestionSetId = defaultQuestionSet?.id ?? null;

        // Auto-populate watchers and assignment from problem config
        const problemConfig = this.problemConfigs.find(config => config.name === this.selectedProblem);

        // Auto-populate watchers
        if (problemConfig?.watchers && problemConfig.watchers.length > 0) {
            this.caseWatchers = [...problemConfig.watchers];
        } else {
            this.caseWatchers = [];
        }

        // Auto-populate assignment
        if (problemConfig?.assignment) {
            this.caseAssignment = { ...problemConfig.assignment };
        } else {
            this.caseAssignment = null;
        }
    }

    onPrescriptionSelected(items: Prescription[]) {
        this.selectedPrescriptions = items;
        // Auto-populate store from first selected prescription
        if (items.length > 0 && !this.selectedStoreId) {
            this.selectedStoreId = items[0].storeID ?? null;
        }
    }

    updateCaseQuestions(updatedQuestions: any[]) {
        this.caseQuestions = updatedQuestions;
    }

    async saveCase(e: BvModalEvent) {
        e.preventDefault();

        if (!this.selectedProblem) {
            this.$notification(NotificationOptions.error('You must select a problem type'));
            return;
        }

        this.showCaseQuestionErrors = false;

        const hasCaseQuestions = !!this.caseQuestions?.length;
        const canEditCaseQuestions = true;

        if (canEditCaseQuestions && hasCaseQuestions) {
            const questionsHaveErrors = this.caseQuestions.some(q => {
                if (!q.text?.trim()) return true;
                if (![UserTaskQuestionType.multipleChoiceMultiple, UserTaskQuestionType.multipleChoiceSingle].includes(q.typeId)) return false;
                if (q.options.some(o => !o.text?.trim())) return true;
                const uniqueTexts = new Set(q.options.map(o => o.text?.trim()));
                if (uniqueTexts.size !== q.options.length) return true;
                return;
            });
            if (questionsHaveErrors) {
                this.$nextTick(() => {
                    this.showCaseQuestionErrors = true;
                });
                this.$notification(NotificationOptions.error('Some questions have errors.'));
                return;
            }
        }

        const answers = this.taskQuestionsComponent?.cleanAnswers();

        if (this.selectedCaseQuestionSetId) {
            if (this.taskQuestionsComponent?.answersHaveErrors()) {
                this.$nextTick(() => {
                    this.showCaseQuestionErrors = true;
                });

                this.$notification(NotificationOptions.error('There are errors on the completion question answers.'));
                return;
            }
        }

        // Combine preselected and selected prescriptions
        const prescriptionsToSave = this.preselectedPrescriptions.length > 0 && this.prescriptions.length === 0
            ? this.preselectedPrescriptions
            : this.selectedPrescriptions;

        const newCaseNote: CaseNote | any = {
            id: 0,
            patientId: this.patient.id,
            problem: this.selectedProblem,
            error: false,
            note: this.problemNote,
            resolvedBy: 'false',
            acceptedBy: 'false',
            questionSetId: this.selectedCaseQuestionSetId,
            categoryName: this.selectedCategory,
            assignment: this.caseAssignment,
            storeId: this.selectedStoreId
        };

        try {
            const response = await axios.post('/Case/', {
                caseProblem: newCaseNote,
                prescriptions: prescriptionsToSave,
                questions: this.caseQuestions,
                answers,
                watchers: this.caseWatchers
            });
            this.$notification(NotificationOptions.success(`Created a new case (id: ${response.data.id})`));
            this.$emit('saved', response.data.id);
            this.$bvModal.hide(this.modalId);
        } catch (err) {
            console.warn(err);
            this.$notification(NotificationOptions.error(err));
        }
    }
}
</script>

<style scoped>
.prescription-selector {
    border: 1px solid #dee2e6;
    border-radius: 4px;
    padding: 0.5rem;
}
</style>
